import { AxiosError } from 'axios'
import { ApiProvider } from '../provider'
import { APIRoutes } from '../routes'
import {
  DeleteCategoryResponseBody,
  NewProduct,
  ProductCategory,
  ProductType,
} from 'api/types/product'
import { UploadFile } from 'pages/Admin/Product'

// Product API service class
class Product {
  // fetch list of product categories
  async getCategories(): Promise<ProductCategory[] | AxiosError | Error> {
    const url = APIRoutes.product.getCategoryList
    return ApiProvider.getRequest(url) as Promise<
      ProductCategory[] | AxiosError | Error
    >
  }

  // create a new product category
  async createCategory({
    name,
    slug,
  }: {
    name: string
    slug: string
  }): Promise<string | AxiosError | Error> {
    const url = APIRoutes.product.createCategory
    const body = {
      name,
      slug,
    }
    return ApiProvider.postRequest(url, body) as Promise<
      string | AxiosError | Error
    >
  }

  // delete a product category
  async deleteCategory({
    id,
  }: {
    id: string
  }): Promise<DeleteCategoryResponseBody | AxiosError | Error> {
    const url = `${APIRoutes.product.deleteCategory}?id=${id}`
    return ApiProvider.deleteRequest(url) as Promise<
      DeleteCategoryResponseBody | AxiosError | Error
    >
  }

  // change a product category
  async changeCategory({
    id,
    name,
    slug,
  }: {
    id: string
    name: string
    slug: string
  }): Promise<string | AxiosError | Error> {
    const url = APIRoutes.product.changeCategory
    const body = {
      id,
      name,
      slug,
    }
    return ApiProvider.postRequest(url, body) as Promise<
      string | AxiosError | Error
    >
  }

  // fetch list of portal products
  async getPortalProductList(): Promise<ProductType[] | AxiosError | Error> {
    const url = APIRoutes.product.getPortalProductList
    return ApiProvider.getRequest(url) as Promise<
      ProductType[] | AxiosError | Error
    >
  }

  // delete a portal product
  async deletePortalProduct({
    product,
  }: {
    product: string
  }): Promise<string | AxiosError | Error> {
    const url = `${APIRoutes.product.deletePortalProduct}?product=${product}`
    return ApiProvider.deleteRequest(url) as Promise<
      string | AxiosError | Error
    >
  }

  // fetch a specific portal product
  async getPortalProduct({
    product,
  }: {
    product: string
  }): Promise<ProductType | AxiosError | Error> {
    const url = `${APIRoutes.product.getPortalProduct}?product=${product}`
    return ApiProvider.getRequest(url) as Promise<
      ProductType | AxiosError | Error
    >
  }

  // save (create or update) a portal product
  async savePortalProduct({
    product,
    categoryId,
    description,
    content,
    simulatorHelperText,
    simulatorConditions,
    status,
    hasSimulation,
    label,
  }: {
    product: string
    categoryId: number
    description: string
    content: string
    simulatorHelperText: string
    simulatorConditions: string
    status: 0 | 1
    hasSimulation: boolean
    label: string
  }): Promise<string | AxiosError | Error> {
    const url = APIRoutes.product.savePortalProduct
    const body = new FormData()
    body.append('product', product)
    body.append('categoryId', categoryId.toString())
    body.append('description', description)
    body.append('content', content)
    body.append('simulatorHelperText', simulatorHelperText)
    body.append('simulatorConditions', simulatorConditions)
    body.append('status', status.toString())
    body.append('hasSimulation', hasSimulation ? 'true' : 'false')
    body.append('label', label)
    return ApiProvider.postRequest(url, body, true) as Promise<
      string | AxiosError | Error
    >
  }

  // fetch products that exist in Isaak but not in the portal
  async getNewProducts(): Promise<NewProduct[] | AxiosError | Error> {
    const url = APIRoutes.product.getNewProducts
    return ApiProvider.getRequest(url) as Promise<
      NewProduct[] | AxiosError | Error
    >
  }

  // add files to a portal product
  async addPortalProductFiles({
    product,
    files,
  }: {
    product: string
    files: UploadFile
  }): Promise<any | AxiosError | Error> {
    const url = APIRoutes.product.addPortalProductFiles
    const formData = new FormData()
    formData.append('product', product)
    formData.append(`productFiles[0].File`, files.file)
    formData.append(`productFiles[0].label`, files.label)
    return ApiProvider.postRequest(url, formData, true) as Promise<
      any | AxiosError | Error
    >
  }

  // remove a file from a portal product
  async removePortalProductFile({
    product,
    fileId,
  }: {
    product: string
    fileId: string
  }): Promise<any | AxiosError | Error> {
    const url = `${APIRoutes.product.removePortalProductFile}?product=${product}&fileId=${fileId}`
    return ApiProvider.deleteRequest(url) as Promise<any | AxiosError | Error>
  }
}

export const productService = new Product()
